/**
 * P14's one copy-members endpoint (`POST /gates/{id}/members/copy`, D37/D115/D118), wrapped over
 * `useSession().request()` — the one authenticated door (`web/src/auth/context.ts`). Sits beside
 * its own screen rather than in `screens/shared/api.ts` for the same reason `screens/devices/api.ts`
 * does: that file is P7's, and this PRD extends P7's screens without taking their files.
 *
 * **This module implements no copy semantics.** Role/schedule/restriction/label carry-over,
 * the managers-toggle's exclude-not-demote rule, the duplicate skip, and the `installer` exclusion
 * are all P14's (`packages/functions/members/copy.ts`) — this file only shapes the request and
 * reads the response, defensively, the same rule every reader in the app follows.
 *
 * **This endpoint returns no share payload, by design** (`copy.test.ts` pins it). A newly copied
 * *new* user's awareness comes from the target gate's own invite context (D37) — `CopyMembersScreen`
 * gets it by calling `inviteMember()` again for one of the newly copied phone numbers: the grant
 * already exists, so `registerGrant()` takes the conditional-write dedupe path and returns
 * `outcome: 'skipped-duplicate'` with no new write and no new audit row, while `invite()` still
 * unconditionally attaches a fresh `share` payload. That reuses P7's own endpoint rather than
 * fabricating share copy client-side, which `share-message.ts`'s own doc comment forbids ("the
 * text is produced by the server as an API result, so it cannot come from a client bundle").
 */

import { buildPath, ROUTES, type E164, type MemberRole } from '@selogate/core'
import type { ApiRequest, ApiResponse } from '../../auth/transport'
import { field, numberField, stringField } from '../../auth/transport'

export type ApiRequestFn = (request: ApiRequest) => Promise<ApiResponse>

export type CopyApiResult<T> =
  | { readonly ok: true; readonly status: number; readonly data: T }
  | { readonly ok: false; readonly status: number; readonly code?: string; readonly field?: string }

function ok<T>(status: number, data: T): CopyApiResult<T> {
  return { ok: true, status, data }
}

function fail<T>(response: ApiResponse): CopyApiResult<T> {
  const code = stringField(response.body, 'code')
  const fieldName = stringField(response.body, 'field')
  return {
    ok: false,
    status: response.status,
    ...(code === undefined ? {} : { code }),
    ...(fieldName === undefined ? {} : { field: fieldName }),
  }
}

function isSuccess(status: number): boolean {
  return status >= 200 && status < 300
}

export interface CopyRow {
  readonly kind: 'member' | 'invite'
  readonly outcome: 'copied' | 'skipped-duplicate' | 'skipped-expired' | 'failed'
  readonly role: MemberRole
  readonly userId?: string
  readonly phone?: E164
  readonly status?: 'active' | 'pending'
}

export interface CopyMembersResult {
  readonly gateId: string
  readonly sourceGateId: string
  readonly rows: readonly CopyRow[]
  readonly summary: {
    readonly copied: number
    readonly skippedDuplicate: number
    readonly skippedExpired: number
    readonly failed: number
  }
}

const COPY_OUTCOMES = new Set(['copied', 'skipped-duplicate', 'skipped-expired', 'failed'])

function readCopyRow(raw: unknown): CopyRow | undefined {
  const kind = field(raw, 'kind')
  const outcome = field(raw, 'outcome')
  const role = field(raw, 'role')
  if (
    (kind !== 'member' && kind !== 'invite') ||
    typeof outcome !== 'string' ||
    !COPY_OUTCOMES.has(outcome) ||
    (role !== 'manager' && role !== 'resident')
  ) {
    return undefined
  }
  const userId = stringField(raw, 'userId')
  const phone = stringField(raw, 'phone')
  const status = field(raw, 'status')
  return {
    kind,
    outcome: outcome as CopyRow['outcome'],
    role,
    ...(userId === undefined ? {} : { userId }),
    ...(phone === undefined ? {} : { phone: phone as E164 }),
    ...(status === 'active' || status === 'pending' ? { status } : {}),
  }
}

function readCopyMembersResult(body: unknown): CopyMembersResult | undefined {
  const gateId = stringField(body, 'gateId')
  const sourceGateId = stringField(body, 'sourceGateId')
  const rowsRaw = field(body, 'rows')
  if (gateId === undefined || sourceGateId === undefined || !Array.isArray(rowsRaw)) return undefined
  const rows = rowsRaw.map(readCopyRow).filter((row): row is CopyRow => row !== undefined)
  const summary = field(body, 'summary')
  return {
    gateId,
    sourceGateId,
    rows,
    summary: {
      copied: numberField(summary, 'copied') ?? 0,
      skippedDuplicate: numberField(summary, 'skippedDuplicate') ?? 0,
      skippedExpired: numberField(summary, 'skippedExpired') ?? 0,
      failed: numberField(summary, 'failed') ?? 0,
    },
  }
}

/**
 * `POST /gates/{targetGateId}/members/copy`. `targetGateId` is the path's `{gateId}`; the
 * source is named in the body — the same request either Access-list entry point builds, just
 * from opposite ends (`copy.ts`'s own doc comment).
 *
 * **`includeManagers` is no longer a client choice.** The screen used to offer a toggle that
 * excluded managers from the copy; every copy now carries every role exactly as the source gate
 * has it, so this always sends `includeManagers: true` — P14's own default when the field is
 * omitted, kept explicit here rather than dropped from the body. Logged as a deviation in
 * `implementation-notes.md` → P15.
 */
export async function copyMembers(
  request: ApiRequestFn,
  targetGateId: string,
  sourceGateId: string,
): Promise<CopyApiResult<CopyMembersResult>> {
  const response = await request({
    method: ROUTES.memberCopy.method,
    path: buildPath(ROUTES.memberCopy, { gateId: targetGateId }),
    body: { sourceGateId, includeManagers: true },
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readCopyMembersResult(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}
